import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { selectEffective, useAppearanceStore } from '../stores/appearanceStore';

const RADIUS_CSS: Record<string, string> = {
  none: '0px',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  full: '9999px',
};

function resolveMode(scheme: 'light' | 'dark' | 'system'): 'light' | 'dark' {
  if (scheme !== 'system') return scheme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveToHex(cssExpression: string): string | null {
  if (!document.body) return null;
  const el = document.createElement('div');
  el.style.cssText =
    `background:${cssExpression};width:1px;height:1px;` +
    `position:fixed;top:0;left:0;pointer-events:none;z-index:-1;`;
  document.body.appendChild(el);
  const raw = getComputedStyle(el).backgroundColor;
  document.body.removeChild(el);

  const rgbMatch = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return (
      '#' +
      rgbMatch
        .slice(1, 4)
        .map((n) => Number(n).toString(16).padStart(2, '0'))
        .join('')
    );
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = raw;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    if (a === 0) return null;
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return null;
  }
}

function syncThemeColor(): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const color = resolveToHex('var(--color-topbar,var(--primary))');
      if (!color) return;
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', color);
    });
  });
}

/**
 * Applies data-mode / data-theme and CSS custom-property overrides to <html>
 * whenever the appearance store changes. Resolves effective values across all
 * layers: previewUi > userPreferences > adminSettings > defaults.
 */
export function useApplyAppearance(): void {
  const {
    colorScheme,
    themeName,
    accentColor,
    topbarColor,
    shellColor,
    globalRadius,
    fieldRadius,
    buttonRadius,
    cardRadius,
    fontSizeScale,
    tableDensity,
    buttonAppearance,
  } = useAppearanceStore(useShallow(selectEffective));

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-mode', resolveMode(colorScheme));
    if (themeName) root.setAttribute('data-theme', themeName);
    else root.removeAttribute('data-theme');
    syncThemeColor();
  }, [colorScheme, themeName]);

  useEffect(() => {
    const root = document.documentElement;
    if (accentColor) {
      root.style.setProperty('--primary', accentColor);
      root.style.setProperty('--color-primary', accentColor);
    } else {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--color-primary');
    }
    if (topbarColor) root.style.setProperty('--color-topbar', topbarColor);
    else root.style.removeProperty('--color-topbar');
    if (shellColor) root.style.setProperty('--color-shell', shellColor);
    else root.style.removeProperty('--color-shell');
    syncThemeColor();
  }, [accentColor, topbarColor, shellColor]);

  useEffect(() => {
    const root = document.documentElement;
    const set = (v: string, key: string) =>
      v && RADIUS_CSS[v]
        ? root.style.setProperty(key, RADIUS_CSS[v])
        : root.style.removeProperty(key);
    set(globalRadius, '--radius');
    set(fieldRadius, '--field-radius');
    set(buttonRadius, '--button-radius');
    set(cardRadius, '--card-radius');
    if (fontSizeScale) root.setAttribute('data-font-scale', fontSizeScale);
    else root.removeAttribute('data-font-scale');
    if (tableDensity) root.setAttribute('data-table-density', tableDensity);
    else root.removeAttribute('data-table-density');
    if (buttonAppearance) root.setAttribute('data-button-appearance', buttonAppearance);
    else root.removeAttribute('data-button-appearance');
  }, [
    globalRadius,
    fieldRadius,
    buttonRadius,
    cardRadius,
    fontSizeScale,
    tableDensity,
    buttonAppearance,
  ]);

  useEffect(() => {
    if (colorScheme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      document.documentElement.setAttribute('data-mode', mq.matches ? 'dark' : 'light');
      syncThemeColor();
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [colorScheme]);
}
